import { type GridListComponentMapProps, GridListLayout } from "@client/content/controls/GridList/component";
import { makeModuleStateSelector, rootContainerPropsSelector } from "@client/content/selectors";
import { getMultiselectValue } from "@client/lib/settings";
import { TargetSelector, combinedSettingsSelector, createShallowEqualSelector } from "@client/selectors";
import { Status } from "@client/types";
import classnames from "classnames";
import { createSelector } from "reselect";
import BlogListItem, { type BlogListItemContainerProps } from "./Item/container";
import { defaultState, previewState } from "./reducer";
import { type BlogList } from "./types";

export const localStateSelector = makeModuleStateSelector<BlogList.State>(defaultState, previewState);

export const makeItemIdsSelector = () =>
  createSelector([localStateSelector], (localState) => localState.items.map((item) => item.id));

export const makeGridListPropsSelector = () => {
  const itemsIdsSelector = makeItemIdsSelector();
  const settingsSelector = combinedSettingsSelector<{
    category: string;
    columns: string;
    imageOrientation: string;
    gap: string;
    layout: GridListLayout;
    pageSize: number;
  }>(TargetSelector.Entity, [
    "category",
    "columns",
    {
      name: "orientation",
      prop: "imageOrientation",
      path: "image"
    },
    "gap",
    "layout",
    "pageSize"
  ]);

  return createShallowEqualSelector(
    [localStateSelector, settingsSelector, itemsIdsSelector, rootContainerPropsSelector],
    (localState, settings, itemsIds, rootContainerProps): GridListComponentMapProps<BlogListItemContainerProps> => ({
      className: classnames({
        "post-thumbnails": settings.imageOrientation === "left",
        "post-thumbnails post-thumbnails-img-right": settings.imageOrientation === "right"
      }),
      hasMore: localState.items.length === settings.pageSize,
      isLoading: localState.status !== Status.Ready,
      layout: settings.layout,
      page: localState.page,
      items: itemsIds,
      columns: +settings.columns,
      gutter: settings.gap,
      pageSize: settings.pageSize,
      itemProps: {
        ...rootContainerProps,
        parentId: rootContainerProps.id
      },
      ItemComponent: BlogListItem
    })
  );
};

export const makeCategoryPageSizeSelector = () =>
  combinedSettingsSelector<{ categories: string[]; pageSize: number }>(TargetSelector.Entity, [
    { name: "category", prop: "categories", converter: getMultiselectValue },
    "pageSize"
  ]);
